import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  CONTENT_FORMATS, CONTENT_PLATFORMS, CONTENT_STATUSES, CREATION_SOURCES,
  CONTENT_STATUS_STYLES,
} from "./constants";

const empty = () => ({
  campaign_id: "", offer_id: "", title: "", topic: "", content_pillar: "",
  content_format: "", platform: "", hook: "", script: "", caption: "",
  cta: "", cta_keyword: "", landing_page_url: "", compliance_notes: "",
  scheduled_at: "", status: "Idea", creation_source: "Manual",
});

export default function ContentQueue() {
  const [items, setItems] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [offers, setOffers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const load = () => {
    const q = {};
    if (statusFilter !== "all") q.status = statusFilter;
    if (platformFilter !== "all") q.platform = platformFilter;
    const qs = new URLSearchParams(q).toString();
    return api.get(`/marketing/content${qs ? `?${qs}` : ""}`).then((r) => setItems(r.data));
  };

  useEffect(() => {
    Promise.all([api.get("/marketing/campaigns"), api.get("/marketing/offers")]).then(([c, o]) => {
      setCampaigns(c.data); setOffers(o.data);
    });
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, platformFilter]);
  useEffect(() => {
    if (params.get("new") === "1") { setForm(empty()); setOpen(true); params.delete("new"); setParams(params, { replace: true }); }
    // eslint-disable-next-line
  }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Content title is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, scheduled_at: form.scheduled_at || null };
      const { data } = await api.post("/marketing/content", payload);
      toast.success("Content created");
      setOpen(false);
      navigate(`/marketing/content/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="content-queue-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-2xl font-bold">Content Queue</h2>
        <Button onClick={() => { setForm(empty()); setOpen(true); }} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="add-content-button">
          <Plus className="h-4 w-4 mr-2" /> Add Content
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mt-6">
        <div className="w-48">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="filter-status" className="focus:ring-[#581C87]"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {CONTENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-48">
          <Select value={platformFilter} onValueChange={setPlatformFilter}>
            <SelectTrigger data-testid="filter-platform" className="focus:ring-[#581C87]"><SelectValue placeholder="Platform" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Platforms</SelectItem>
              {CONTENT_PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-6 bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-widest text-gray-500 font-semibold">
          <div className="col-span-5">Title</div>
          <div className="col-span-2">Format</div>
          <div className="col-span-2">Platform</div>
          <div className="col-span-3">Status</div>
        </div>
        {items === null ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#581C87]" /></div>
        ) : items.length === 0 ? (
          <div className="px-6 py-10 text-center text-gray-400 text-sm" data-testid="content-empty">No content items match your filters.</div>
        ) : (
          items.map((it) => (
            <button key={it.id} onClick={() => navigate(`/marketing/content/${it.id}`)}
              className="w-full text-left grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-4 border-b border-gray-100 items-center hover:bg-gray-50 transition-colors"
              data-testid={`content-row-${it.id}`}>
              <div className="md:col-span-5">
                <p className="font-medium text-sm text-gray-900">{it.title}</p>
                <p className="text-xs text-gray-400 mt-0.5">{it.topic || "—"}</p>
              </div>
              <div className="md:col-span-2 text-sm text-gray-500">{it.content_format || "—"}</div>
              <div className="md:col-span-2 text-sm text-gray-500">{it.platform || "—"}</div>
              <div className="md:col-span-3">
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${CONTENT_STATUS_STYLES[it.status] || "bg-gray-100 text-gray-600"}`}>{it.status}</span>
              </div>
            </button>
          ))
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="content-form-dialog">
          <DialogHeader><DialogTitle className="font-heading">Add Content</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Content Title *</Label><Input value={form.title} onChange={(e) => upd("title", e.target.value)} data-testid="content-title-input" className="focus-visible:ring-[#581C87]" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Campaign</Label>
                <Select value={form.campaign_id || "none"} onValueChange={(v) => upd("campaign_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="content-campaign-select" className="focus:ring-[#581C87]"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">No campaign</SelectItem>{campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Offer</Label>
                <Select value={form.offer_id || "none"} onValueChange={(v) => upd("offer_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="content-offer-select" className="focus:ring-[#581C87]"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">No offer</SelectItem>{offers.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Topic</Label><Input value={form.topic} onChange={(e) => upd("topic", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
              <div className="space-y-2"><Label>Content Pillar</Label><Input value={form.content_pillar} onChange={(e) => upd("content_pillar", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
              <div className="space-y-2">
                <Label>Content Format</Label>
                <Select value={form.content_format || "none"} onValueChange={(v) => upd("content_format", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="content-format-select" className="focus:ring-[#581C87]"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{CONTENT_FORMATS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Platform</Label>
                <Select value={form.platform || "none"} onValueChange={(v) => upd("platform", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="content-platform-select" className="focus:ring-[#581C87]"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{CONTENT_PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>Hook</Label><Textarea value={form.hook} onChange={(e) => upd("hook", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
            <div className="space-y-2"><Label>Main Script / Copy</Label><Textarea value={form.script} onChange={(e) => upd("script", e.target.value)} className="focus-visible:ring-[#581C87] min-h-[100px]" /></div>
            <div className="space-y-2"><Label>Caption</Label><Textarea value={form.caption} onChange={(e) => upd("caption", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>CTA</Label><Input value={form.cta} onChange={(e) => upd("cta", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
              <div className="space-y-2"><Label>CTA Keyword</Label><Input value={form.cta_keyword} onChange={(e) => upd("cta_keyword", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
            </div>
            <div className="space-y-2"><Label>Landing-page URL</Label><Input value={form.landing_page_url} onChange={(e) => upd("landing_page_url", e.target.value)} placeholder="https://..." className="focus-visible:ring-[#581C87]" /></div>
            <div className="space-y-2"><Label>Compliance Notes</Label><Textarea value={form.compliance_notes} onChange={(e) => upd("compliance_notes", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => upd("status", v)}>
                  <SelectTrigger data-testid="content-status-select" className="focus:ring-[#581C87]"><SelectValue /></SelectTrigger>
                  <SelectContent>{CONTENT_STATUSES.filter((s) => s !== "Generating" && s !== "Published" && s !== "Failed").map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Creation Source</Label>
                <Select value={form.creation_source} onValueChange={(v) => v === "Manual" && upd("creation_source", v)}>
                  <SelectTrigger data-testid="content-source-select" className="focus:ring-[#581C87]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Manual">Manual</SelectItem>
                    <SelectItem value="AI Generated" disabled>AI Generated — created by the Campaign Director</SelectItem>
                    <SelectItem value="AI Variation" disabled>AI Variation — created by the Campaign Director</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="content-save-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Content"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
